/*
 * ==============================================================================
 *
 * Copyright (c) 2023. Koninklijke Philips Electronics N.V.
 *
 * All rights are reserved. Reproduction in whole or in part is prohibited without the prior written consent of the copyright holder.
 *
 * ==============================================================================
 */

import React, { useContext, useEffect, useState } from "react";
import { PatientsContext } from ".";
import { useToasts } from "react-toast-notifications";
import fetchHelper from "../../services/fetchHelper";
import { useParams } from "react-router-dom";
import isEmpty from "lodash/isEmpty";
import "./AssociatedHospitals.scss";
import savingSpinner from "../../assets/esign-ui-icons/saving-ico-white.svg";
import { LayoutContext } from "../../components/layout";

const AssociatedHospitals = () => {
  const { patient, setPatient } = useContext(PatientsContext);
  const { user } = useContext(LayoutContext);
  const { addToast } = useToasts();
  const [hospital, setHospital] = useState(null);
  const [mrn, setMRN] = useState("");
  const [isEdit, setIsEdit] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState(false);
  const [existingMrn, setExistingMrn] = useState(null);
  const { i } = useParams();

  const fetchHospital = async () => {
    try {
      const data = await fetchHelper.get({
        url: "/patientmanagement/rest/patientlocation/findByPatientId/" + i,
      });
      setMRN(data[0].mrn);
      !data[0].mrn ? setExistingMrn("") : setExistingMrn(data[0].mrn);
      setHospital({ data });
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    fetchHospital();
  }, []);

  const save = async (e) => {
    if (isEmpty(mrn?.trim())) {
      setErrors(true);
      return false;
    }
    e.preventDefault();
    setSaving(true);
    try {
      const data = await fetchHelper.put("/patientmanagement/rest/patientlocation/" + hospital?.data[0]?.id, {
        mrn,
        id: hospital?.data[0]?.id.toString(),
        patient: { id: i },
        location: { id: hospital?.data[0]?.location?.id },
      });
      setMRN(data.mrn);
      setExistingMrn(data.mrn);
      setIsEdit(false);
      const p = { ...patient };
      if (p.patientLocation) {
        p.patientLocation[0].mrn = data.mrn;
        setPatient(p);
      }
    } catch (err) {
      console.log(err);
      err.errors.forEach((err) => {
        addToast(err.msg, {
          appearance: "error",
          autoDismiss: false,
        });
      });
    }
    setSaving(false);
    setErrors(false);
  };
  return (
    <div className="tabContent location">
      <div className="content">
        <div className="grpHdrPg">
          <h3>
            Patient Profile: <span>John Smith</span>
          </h3>
        </div>
        <div className="section-header-associated">
          <div className="section-header-title">
            <h3 data-testid="associated-hospital-location">Location</h3>
            <span className="message danger-message">* Indicates Required Field</span>
          </div>
          {patient?.patientState === "LOCKED" && !user?.isServiceReport ? (
            <div style={{ fontSize: 14, marginBottom: 20 }}>
              * Patient information is in sync with EMR record. Please update EMR directly if changes to
              patient personal information are needed.
            </div>
          ) : null}
        </div>
        {hospital?.data ? (
          <div className="row rowStretch location-row">
            <div className="col col-s-100 col33">
              <label>
                <span>Location</span>
                <input type="text" value={hospital?.data[0]?.location?.name} disabled={!isEdit} />
              </label>
            </div>
            <div className="col col-s-100 col33">
              <label>
                <span>
                  MRN
                  <sub className="required-field">*</sub>
                </span>
                <input type="text" value={mrn} onChange={(e) => setMRN(e.target.value)} disabled={!isEdit} />
              </label>
              {errors && (
                <span className="error">Required fields missing data. Please review and re-submit.</span>
              )}
            </div>
            <div className="col col-s-100 col33" style={{ display: "flex", marginTop: "12px" }}>
              {!isEdit ? (
                <button
                  className="btn btnTeal btn-edit"
                  onClick={() => setIsEdit(true)}
                  disabled={patient.patientState === "LOCKED" && !user?.isServiceReport}
                >
                  Edit
                </button>
              ) : (
                <>
                  <button className="btn btnTeal btn-edit" onClick={save}>
                    {saving ? (
                      <img id="loading-spinner" src={savingSpinner} style={{ width: 15, height: 15 }} />
                    ) : null}{" "}
                    Save
                  </button>
                  <button
                    className="btn"
                    onClick={() => {
                      setIsEdit(false);
                      setErrors(false);
                      setMRN(existingMrn);
                    }}
                  >
                    Cancel
                  </button>
                </>
              )}
            </div>
          </div>
        ) : (
          <span>Loading</span>
        )}
      </div>
    </div>
  );
};

export default AssociatedHospitals;
